import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { RotateCcw } from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import {
  Field,
  FieldContent,
  FieldError,
  FieldLabel,
} from "@/components/ui/field";
import { Input } from "@/components/ui/input";
import { cn } from "@/lib/utils";
import type { RetailCompany } from "@/lib/cat/retail-client.types";
import {
  getSiretLookupErrorMessage,
  isValidSiretFormat,
  SIRET_FORMAT_VALIDATION_MESSAGE,
  SIRET_MDM_DEMO_MODE_NOTE,
  type SiretLookupResult,
} from "@/lib/cat/client-step-contracts";

function RetailCompanySection({
  isVerifying = false,
  newSearchButtonLabel = "Nouvelle recherche",
  onResetSearch,
  onSiretChange,
  onVerify,
  siretInput,
  siretResult,
  validatedCompany,
}: {
  isVerifying?: boolean;
  newSearchButtonLabel?: string;
  onResetSearch?: () => void;
  onSiretChange: (v: string) => void;
  onVerify: () => void;
  siretInput: string;
  siretResult: SiretLookupResult | null;
  validatedCompany: RetailCompany | null;
}) {
  const idPrefix = "retail-company";
  const siretErrorMessage = getSiretLookupErrorMessage(siretResult);
  const mdmWarning =
    siretResult?.kind === "known"
      ? (siretResult.mdmReferentialWarning ?? null)
      : null;
  const showInseeMdmDetail =
    Boolean(validatedCompany) && siretResult?.kind === "known";
  const canLaunchSiretSearch = isValidSiretFormat(siretInput);
  const showSiretFormatHint = siretInput.length > 0 && !canLaunchSiretSearch;
  const searchSucceededWithResult =
    siretResult?.kind === "known" && Boolean(validatedCompany) && onResetSearch;
  const showSearchForm = !searchSucceededWithResult;

  return (
    <Card>
      <CardHeader
        className={cn(
          searchSucceededWithResult &&
            "flex flex-row flex-wrap items-center justify-between gap-2 space-y-0",
        )}
      >
        <CardTitle className="text-base">Société</CardTitle>
        {searchSucceededWithResult ? (
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={onResetSearch}
          >
            <RotateCcw className="mr-2 size-4" aria-hidden />
            {newSearchButtonLabel}
          </Button>
        ) : null}
      </CardHeader>
      <CardContent className="space-y-4">
        {showSearchForm ? (
          <div className="flex flex-col gap-4 sm:flex-row sm:items-end">
            <Field
              className="min-w-0 flex-1"
              data-invalid={showSiretFormatHint ? true : undefined}
            >
              <FieldLabel htmlFor={`${idPrefix}-siret`}>
                N° de SIRET client
              </FieldLabel>
              <FieldContent>
                <Input
                  aria-invalid={showSiretFormatHint}
                  autoComplete="off"
                  id={`${idPrefix}-siret`}
                  inputMode="numeric"
                  maxLength={14}
                  value={siretInput}
                  onChange={(e) =>
                    onSiretChange(
                      e.target.value.replace(/\D/g, "").slice(0, 14),
                    )
                  }
                />
                {showSiretFormatHint ? (
                  <FieldError>{SIRET_FORMAT_VALIDATION_MESSAGE}</FieldError>
                ) : null}
              </FieldContent>
            </Field>
            <Button
              disabled={isVerifying || !canLaunchSiretSearch}
              type="button"
              onClick={onVerify}
            >
              {isVerifying ? "Vérification…" : "Vérifier le SIRET"}
            </Button>
          </div>
        ) : null}

        {siretErrorMessage ? (
          <Alert variant="destructive">
            <AlertTitle>Point bloquant</AlertTitle>
            <AlertDescription>{siretErrorMessage}</AlertDescription>
          </Alert>
        ) : null}

        {mdmWarning ? (
          <Alert>
            <AlertTitle>Référentiel interne</AlertTitle>
            <AlertDescription className="space-y-3">
              <p>{mdmWarning}</p>
              <p className="text-muted-foreground text-sm">
                {SIRET_MDM_DEMO_MODE_NOTE}
              </p>
              <Button
                type="button"
                variant="secondary"
                onClick={() => {
                  // Not wired to create flow yet
                }}
              >
                Créer dans le référentiel interne
              </Button>
            </AlertDescription>
          </Alert>
        ) : null}

        {showInseeMdmDetail && validatedCompany ? (
          <div className="bg-muted/40 space-y-4 rounded-lg border p-4 text-sm">
            <div className="flex flex-wrap items-center gap-2">
              <span className="font-medium">Société</span>
              {validatedCompany.tag ? (
                <Badge variant="secondary">{validatedCompany.tag}</Badge>
              ) : null}
            </div>
            <p>
              <span className="text-muted-foreground">SIRET : </span>
              <span className="font-mono">{validatedCompany.siret}</span>
            </p>

            <div className="space-y-2 border-t pt-3">
              <p className="text-muted-foreground text-xs font-semibold uppercase">
                INSEE
              </p>
              <p>
                <span className="text-muted-foreground">Raison sociale : </span>
                {validatedCompany.inseeDenomination ?? "—"}
              </p>
              <p>
                <span className="text-muted-foreground">Code APE : </span>
                {validatedCompany.inseeApe ?? validatedCompany.ape}
              </p>
            </div>

            {validatedCompany.mdmDenomination ||
            validatedCompany.mdmApe ||
            validatedCompany.mdmAddressLine ? (
              <div className="space-y-2 border-t pt-3">
                <p className="text-muted-foreground text-xs font-semibold uppercase">
                  Référentiel MDM
                </p>
                <p>
                  <span className="text-muted-foreground">
                    Raison sociale :{" "}
                  </span>
                  {validatedCompany.mdmDenomination ?? "—"}
                </p>
                <p>
                  <span className="text-muted-foreground">Code APE : </span>
                  {validatedCompany.mdmApe ?? "—"}
                </p>
                <p>
                  <span className="text-muted-foreground">Adresse : </span>
                  {validatedCompany.mdmAddressLine ?? "—"}
                </p>
                <p>
                  <span className="text-muted-foreground">
                    Code postal / Ville :{" "}
                  </span>
                  {validatedCompany.mdmPostalCode ?? "—"}{" "}
                  {validatedCompany.mdmCity ?? ""}
                </p>
              </div>
            ) : null}

            <p className="text-muted-foreground border-t pt-2 text-xs">
              INSEE : OK — Atomium / MDM : {validatedCompany.mdmAtomium}
            </p>
          </div>
        ) : null}
      </CardContent>
    </Card>
  );
}

export { RetailCompanySection };
